import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, Polygon, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { AmenitiesResponse } from '../../services/api';
import { SelectedAmenityTypes, ProjectDetailsTab } from '../../../types';

interface ProjectMapProps {
  latitude: number;
  longitude: number;
  tab: ProjectDetailsTab;
  amenitiesData: AmenitiesResponse | null;
  radius: number;
  selectedAmenityTypes: SelectedAmenityTypes;
}

/** GeoJSON [lng, lat] ring -> Leaflet [lat, lng] positions. */
const toLatLngs = (ring: Array<[number, number]>): [number, number][] =>
  ring.map(([lng, lat]) => [lat, lng] as [number, number]);

const ProjectMap: React.FC<ProjectMapProps> = ({ latitude, longitude, tab, amenitiesData, radius, selectedAmenityTypes }) => {
  const mapRef = useRef<L.Map | null>(null);

  // The site outline and the area the backend actually filtered against. Both
  // are absent when OneMap has no land lot for the pin, in which case the map
  // falls back to the old circle drawn round the point.
  const lotRings = amenitiesData?.project?.boundary ?? null;
  const searchAreaRing = amenitiesData?.project?.searchArea ?? null;

  useEffect(() => {
    if (mapRef.current) {
      if (tab === 'amenities') {
        // Fit to the real search area when we have it — it is off-centre from
        // the pin, so a circle's bounds would crop one side.
        if (searchAreaRing && searchAreaRing.length > 2) {
          mapRef.current.fitBounds(L.latLngBounds(toLatLngs(searchAreaRing)), { padding: [20, 20] });
        } else {
          const center = L.latLng(latitude, longitude);
          mapRef.current.fitBounds(center.toBounds(radius * 2), { padding: [20, 20] });
        }
      } else {
        mapRef.current.setView([latitude, longitude], 15);
      }
    }
  }, [latitude, longitude, tab, radius, searchAreaRing]);

  // Component to handle map instance
  const MapController = () => {
    const map = useMap();
    useEffect(() => {
      mapRef.current = map;
    }, [map]);
    return null;
  };

  // Create custom icons for different amenity types
  const createIcon = (color: string) => {
    return L.divIcon({
      className: 'custom-marker',
      html: `<div style="background-color: ${color}; width: 20px; height: 20px; border-radius: 50%; border: 2px solid var(--color-white); box-shadow: 0 2px 4px rgba(var(--color-shadow-rgb), 0.3);"></div>`,
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });
  };

  // Building icon for project marker - using SVG
  const projectIcon = L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: var(--color-info); width: 32px; height: 32px; border-radius: 4px; border: 2px solid var(--color-white); box-shadow: 0 2px 4px rgba(var(--color-shadow-rgb), 0.3); display: flex; align-items: center; justify-content: center;">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="var(--color-white)" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
      </svg>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });

  return (
    <MapContainer
      center={[latitude, longitude]}
      zoom={15}
      style={{ height: '100%', width: '100%', minHeight: 400 }}
    >
      <MapController />
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      
      {/* Project marker - always shown */}
      <Marker position={[latitude, longitude]} icon={projectIcon}>
        <Popup>
          <strong>Project Location</strong>
        </Popup>
      </Marker>

      {/* Search area - the site grown outwards by the radius, which is what the
          amenity list was filtered against. Falls back to a circle round the pin
          when OneMap has no land lot for this project. */}
      {tab === 'amenities' && (
        searchAreaRing && searchAreaRing.length > 2 ? (
          <Polygon
            positions={toLatLngs(searchAreaRing)}
            pathOptions={{
              color: 'var(--color-info)',
              fillColor: 'var(--color-info)',
              fillOpacity: 0.1,
              weight: 2,
            }}
          />
        ) : (
          <Circle
            center={[latitude, longitude]}
            radius={radius}
            pathOptions={{
              color: 'var(--color-info)',
              fillColor: 'var(--color-info)',
              fillOpacity: 0.1,
              weight: 2,
            }}
          />
        )
      )}

      {/* Site outline - the land lot the distances are measured from */}
      {tab === 'amenities' && lotRings?.map((ring, i) => (
        <Polygon
          key={`lot-${i}`}
          positions={toLatLngs(ring)}
          pathOptions={{
            color: 'var(--color-warning)',
            fillColor: 'var(--color-warning)',
            fillOpacity: 0.35,
            weight: 2,
          }}
        />
      ))}

      {/* Amenities markers - only shown in amenities tab */}
      {tab === 'amenities' && amenitiesData && (
        <>
          {/* MRT/LRT Stations */}
          {selectedAmenityTypes.mrtLrt && amenitiesData.amenities.mrtLrt.map((station) => (
            <Marker
              key={`mrt-${station.id}`}
              position={[station.latitude, station.longitude]}
              icon={createIcon('var(--color-danger)')}
            >
              <Popup>
                <strong>{station.name}</strong>
                <br />
                {station.mrtLine && <span>{station.mrtLine}</span>}
                <br />
                <span>{Math.round(station.distance)}m away</span>
              </Popup>
            </Marker>
          ))}

          {/* Schools */}
          {selectedAmenityTypes.schools && amenitiesData.amenities.schools.map((school) => (
            <Marker
              key={`school-${school.id}`}
              position={[school.latitude, school.longitude]}
              icon={createIcon('var(--color-success)')}
            >
              <Popup>
                <strong>{school.name}</strong>
                <br />
                {school.type && <span>{school.type}</span>}
                <br />
                <span>{Math.round(school.distance)}m away</span>
              </Popup>
            </Marker>
          ))}

          {/* Preschools */}
          {selectedAmenityTypes.preschools && amenitiesData.amenities.preschools.map((preschool) => (
            <Marker
              key={`preschool-${preschool.id}`}
              position={[preschool.latitude, preschool.longitude]}
              icon={createIcon('var(--color-secondary)')}
            >
              <Popup>
                <strong>{preschool.name}</strong>
                <br />
                {preschool.type && <span>{preschool.type}</span>}
                <br />
                {preschool.address && <span>{preschool.address}</span>}
                <br />
                <span>{Math.round(preschool.distance)}m away</span>
              </Popup>
            </Marker>
          ))}

          {/* Supermarkets */}
          {selectedAmenityTypes.supermarkets && amenitiesData.amenities.supermarkets.map((amenity) => (
            <Marker
              key={`supermarket-${amenity.id}`}
              position={[amenity.latitude, amenity.longitude]}
              icon={createIcon('var(--color-warning)')}
            >
              <Popup>
                <strong>{amenity.name}</strong>
                <br />
                {amenity.address && <span>{amenity.address}</span>}
                <br />
                <span>{Math.round(amenity.distance)}m away</span>
              </Popup>
            </Marker>
          ))}
        </>
      )}
    </MapContainer>
  );
};

export default ProjectMap;
